import React from "react";
import { BrowserRouter as Router, Routes, Route } from "react-router-dom";
import "./App.css";
import './styles/main.css';
import "font-awesome/css/font-awesome.min.css";
import Login from "./pages/Login";
import BrokerSettings from "./pages/BrokerSettings";
import Register from "./pages/Register";
import Home from "./pages/Home";
import Dashboard from "./pages/Dashboard";
import Profile from "./pages/Profile";
import ChangePassword from "./pages/ChangePassword";
import SupportChat from "./pages/SupportChat";
import AddFunds from "./pages/AddFunds";
import Withdrawal from "./pages/Withdrawal";
import ActivateId from "./pages/ActivateId";
import MyDirectTeam from "./pages/MyDirectTeam";
import AllTeam from "./pages/AllTeam";
import LevelTree from "./pages/LevelTree";
import IncomeReport from "./pages/IncomeReport";
import WithdrawalHistory from "./pages/WithdrawalHistory";
import DepositHistory from "./pages/DepositHistory";
import BotHistory from './pages/BotActivationHistory';
import { ThemeProvider } from "./contexts/ThemeContext";
import { WalletProvider } from './context/WalletContext';
import AddFunds2 from "./pages/AddFunds2";
import Statement1 from './pages/Statement1';
import Statement2 from './pages/Statement2';
import Statement3 from './pages/Statement3';
import BotActivationHistory from './pages/BotHistory';

// ✅ Add Error Boundary Component
class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null, errorInfo: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, errorInfo) {
    console.error('❌ React Error:', error);
    console.error('❌ Error Info:', errorInfo);
    this.setState({ errorInfo });
  }

  render() {
    if (this.state.hasError) {
      return (
        <div style={{ 
          padding: '50px 20px', 
          textAlign: 'center',
          minHeight: '100vh',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'center',
          backgroundColor: '#f7fafc'
        }}>
          <div style={{ 
            maxWidth: '500px',
            padding: '40px',
            backgroundColor: 'white',
            borderRadius: '12px',
            boxShadow: '0 4px 20px rgba(0,0,0,0.1)'
          }}>
            <h2 style={{ color: '#e53e3e', marginBottom: '10px' }}>
              <i className="fa fa-exclamation-triangle"></i> Something Went Wrong
            </h2>
            <p style={{ color: '#718096', marginBottom: '20px' }}>
              An error occurred while loading the application.
            </p>
            <details style={{ 
              textAlign: 'left', 
              background: '#f7fafc', 
              padding: '10px',
              borderRadius: '8px',
              marginBottom: '20px'
            }}>
              <summary style={{ cursor: 'pointer', fontWeight: 'bold' }}>Error Details</summary>
              <pre style={{ 
                whiteSpace: 'pre-wrap', 
                wordBreak: 'break-word',
                fontSize: '12px',
                color: '#4a5568',
                marginTop: '10px'
              }}>
                {this.state.error?.toString()}
              </pre>
            </details>
            <button 
              onClick={() => window.location.reload()}
              style={{
                padding: '10px 30px',
                background: 'linear-gradient(135deg, #667eea, #764ba2)',
                color: 'white',
                border: 'none',
                borderRadius: '8px',
                cursor: 'pointer',
                fontSize: '16px',
                fontWeight: '600'
              }}
            >
              <i className="fa fa-refresh"></i> Refresh Page
            </button>
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}

// ✅ Add Route Error Boundary
const RouteErrorBoundary = ({ children }) => {
  return <ErrorBoundary>{children}</ErrorBoundary>;
};

const App = () => {
  return (
    <ErrorBoundary>
      <ThemeProvider>
        <WalletProvider>
          <Router>
            <Routes>
              {/* Public Routes */}
              <Route path="/" element={
                <RouteErrorBoundary>
                  <Home />
                </RouteErrorBoundary>
              } />
              <Route path="/login" element={
                <RouteErrorBoundary>
                  <Login />
                </RouteErrorBoundary>
              } />
              <Route path="/register" element={
                <RouteErrorBoundary>
                  <Register />
                </RouteErrorBoundary>
              } />
              
              {/* User Panel Routes */}
              <Route path="/dashboard" element={
                <RouteErrorBoundary>
                  <Dashboard />
                </RouteErrorBoundary>
              } />
              <Route path="/profile" element={
                <RouteErrorBoundary>
                  <Profile />
                </RouteErrorBoundary>
              } />
              <Route path="/change-password" element={
                <RouteErrorBoundary>
                  <ChangePassword />
                </RouteErrorBoundary>
              } />
              <Route path="/support" element={
                <RouteErrorBoundary>
                  <SupportChat />
                </RouteErrorBoundary>
              } />
              <Route path="/add-funds" element={
                <RouteErrorBoundary>
                  <AddFunds />
                </RouteErrorBoundary>
              } />
              <Route path="/add-funds2" element={
                <RouteErrorBoundary>
                  <AddFunds2 />
                </RouteErrorBoundary>
              } />
              <Route path="/withdrawal" element={
                <RouteErrorBoundary>
                  <Withdrawal />
                </RouteErrorBoundary>
              } />
              <Route path="/activate-id" element={
                <RouteErrorBoundary>
                  <ActivateId />
                </RouteErrorBoundary>
              } />
              <Route path="/my-direct-team" element={
                <RouteErrorBoundary>
                  <MyDirectTeam />
                </RouteErrorBoundary>
              } />
              <Route path="/all-team" element={
                <RouteErrorBoundary>
                  <AllTeam />
                </RouteErrorBoundary>
              } />
              <Route path="/level-tree" element={
                <RouteErrorBoundary>
                  <LevelTree />
                </RouteErrorBoundary>
              } />
              <Route path="/broker-settings" element={
                <RouteErrorBoundary>
                  <BrokerSettings />
                </RouteErrorBoundary>
              } />
              <Route path="/bot-history" element={
                <RouteErrorBoundary>
                  <BotHistory />
                </RouteErrorBoundary>
              } />
              
              {/* Income Report Routes */}
              <Route path="/income-report" element={
                <RouteErrorBoundary>
                  <IncomeReport />
                </RouteErrorBoundary>
              } />
              <Route path="/income-report/:incometype" element={
                <RouteErrorBoundary>
                  <IncomeReport />
                </RouteErrorBoundary>
              } />
              
              {/* History Routes */}
              <Route path="/withdrawal-history" element={
                <RouteErrorBoundary>
                  <WithdrawalHistory />
                </RouteErrorBoundary>
              } />
              <Route path="/deposit-history" element={
                <RouteErrorBoundary>
                  <DepositHistory />
                </RouteErrorBoundary>
              } />
              <Route path="/main-wallet-history" element={
                <RouteErrorBoundary>
                  <Statement1 />
                </RouteErrorBoundary>
              } />
              <Route path="/deposit-wallet-history" element={
                <RouteErrorBoundary>
                  <Statement2 />
                </RouteErrorBoundary>
              } />
              <Route path="/fuel-wallet-history" element={
                <RouteErrorBoundary>
                  <Statement3 />
                </RouteErrorBoundary>
              } />
              <Route path="/bot-activation-history" element={
                <RouteErrorBoundary>
                  <BotActivationHistory />
                </RouteErrorBoundary>
              } />
              
              {/* Catch all - Redirect to home */}
              <Route path="*" element={
                <RouteErrorBoundary>
                  <Home />
                </RouteErrorBoundary>
              } />
            </Routes>
          </Router>
        </WalletProvider>
      </ThemeProvider>
    </ErrorBoundary>
  );
};

export default App; 